/**
 * A recording, as Azure's short-audio API takes it: WAV, 16-bit PCM, 16 kHz,
 * one channel.
 *
 * WHY CONVERT IN THE BROWSER. MediaRecorder gives each browser's own idea of
 * a recording - webm/opus in Chrome, Edge and Firefox, mp4/aac in Safari -
 * and Azure's short-audio REST takes WAV PCM or Ogg Opus only. Decoding what
 * MediaRecorder made (decodeAudioData reads its own browser's format) and
 * rendering it again through an OfflineAudioContext at 16 kHz mono gives
 * the one format every browser can make and Azure always takes. Thirty
 * seconds of it is about 960 KB.
 *
 * Pure apart from the two audio contexts; encodeWav is plain arithmetic, so
 * e2e/todo-assist.mjs checks it in Node.
 */

export const RATE = 16000;

/** Float samples (-1..1) as a 16-bit PCM mono WAV, in bytes. */
export function encodeWav(samples, rate = RATE) {
  const n = samples.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const text = (at, s) => { for (let i = 0; i < s.length; i += 1) v.setUint8(at + i, s.charCodeAt(i)); };
  text(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); text(8, 'WAVE');
  text(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  text(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i] || 0));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Uint8Array(buf);
}

/**
 * A recorded Blob (whatever MediaRecorder made) as a 16 kHz mono WAV Blob.
 * Resolves { blob, seconds }; rejects when the browser cannot read its own
 * recording, which the caller turns into words.
 */
export async function toWav16k(recorded) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const bytes = await recorded.arrayBuffer();
  const ctx = new AC();
  let decoded;
  try {
    decoded = await new Promise((resolve, reject) => {
      // Old Safari has only the callback form.
      const p = ctx.decodeAudioData(bytes, resolve, reject);
      if (p && typeof p.then === 'function') p.then(resolve, reject);
    });
  } finally {
    try { ctx.close(); } catch { /* already closed */ }
  }
  const frames = Math.max(1, Math.ceil(decoded.duration * RATE));
  const off = new OAC(1, frames, RATE);
  const src = off.createBufferSource();
  src.buffer = decoded;   // more channels than one are mixed down by the destination
  src.connect(off.destination);
  src.start(0);
  const rendered = await off.startRendering();
  const wav = encodeWav(rendered.getChannelData(0), RATE);
  return { blob: new Blob([wav], { type: 'audio/wav' }), seconds: decoded.duration };
}
